import * as React from 'react';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe, constFalse } from 'fp-ts/function';
import { OperationContext } from 'urql';
import {
  useSetDefaultTeamMutation,
  useTeamQuery,
  FullTeamFragment,
} from '../generated/graphql';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useTeamIdParam } from '../hooks/useTeamIdQueryParam';
import { User } from './AuthContext';

interface TeamContextState {
  team: O.Option<FullTeamFragment>;
  switchTeam: (teamId: string) => Promise<void>;
  fetching: boolean;
  refetchTeam: (opts?: Partial<OperationContext>) => void;
  isSelecting: boolean;
}

export const TeamContext = React.createContext<TeamContextState>({
  team: O.none,
  switchTeam: () => Promise.resolve(),
  fetching: false,
  refetchTeam: () => null,
  isSelecting: false,
});

interface Props {
  user?: Pick<User, 'teams'>;
}

export const TeamProvider: React.FC<Props> = ({ user, children }) => {
  const { track } = useAnalytics();
  const [, setDefaultTeam] = useSetDefaultTeamMutation();

  // The users chosen team is always the first one in the list.
  // When we update the chosen team the order in which the teams
  // are returned will update accordingly.
  const userTeamIds = pipe(
    user?.teams,
    O.fromNullable,
    O.map((teams) => teams.map((team) => team.id)),
  );

  const teamIdFromQuery = useTeamIdParam();

  // if there's a team ID in the querystring, try and use that as
  // the chosen team. Otherwise fall back on the first user team ID.
  const chosenTeamId = pipe(
    teamIdFromQuery,
    O.fold(() => pipe(userTeamIds, O.chain(A.head)), O.some),
  );

  const [teamResponse, refetchTeam] = useTeamQuery({
    variables: { id: O.getOrElse(() => '')(chosenTeamId) },
    pause: O.isNone(chosenTeamId),
  });

  const [isSelecting, setIsSelecting] = React.useState(false);

  const switchTeam = (teamId: string): Promise<void> => {
    const isChosenAlready = () =>
      pipe(
        userTeamIds,
        O.chain(A.head),
        O.map((id) => id === teamId),
        O.getOrElse(constFalse),
      );

    const isOnTeam = () =>
      pipe(
        userTeamIds,
        O.map((ids) => ids.includes(teamId)),
        O.getOrElse(constFalse),
      );

    if (isChosenAlready() || !isOnTeam()) {
      return Promise.resolve();
    }

    setIsSelecting(true);
    track('Team Switched', { selectedTeam: teamId });
    return setDefaultTeam({ teamId: parseInt(teamId, 10) })
      .then(() => undefined)
      .catch(() => {
        track('Data Submit Failed', { form: 'switch_team' });
        // do nothing. It's OK if this fails
      })
      .finally(() => setIsSelecting(false));
  };

  React.useEffect(() => {
    // switch team in the background when the chosen teamId changes.
    // this will be a noop if the chosen team is already selected.
    if (O.isSome(chosenTeamId)) {
      switchTeam(chosenTeamId.value);
    }
  }, [O.toNullable(chosenTeamId)]);

  return (
    <TeamContext.Provider
      value={{
        team: O.fromNullable(teamResponse.data?.team),
        switchTeam,
        fetching: teamResponse.fetching,
        refetchTeam,
        isSelecting,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  return React.useContext(TeamContext);
};
